import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { keepAlive } from '@core/http/keep-alive';

/**
 * Opt-in cancellation for a request this interceptor would otherwise protect.
 * Set it only when aborting the request client-side is genuinely harmless.
 */
export const ALLOW_REQUEST_CANCELLATION = new HttpContextToken<boolean>(() => false);

/** Safe methods: aborting them leaves no server state half-applied. */
const CANCELLABLE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Keeps writes on the wire when their subscriber tears down.
 *
 * A `POST` / `PUT` / `PATCH` / `DELETE` that already reached the server is not
 * undone by aborting the XHR — the client only loses the response, and Chrome
 * surfaces the loss as a CORS error on cross-origin calls. Cancelling a write
 * therefore buys nothing and hides the outcome, so unsafe methods run to
 * completion regardless of who unsubscribes.
 *
 * Reads stay cancellable: aborting a superseded `GET` is the point of
 * `switchMap` and of tearing down on navigation.
 *
 * Registered first so the protection covers every interceptor below it,
 * including the `AuthExpiredInterceptor` refresh-and-retry.
 */
@Injectable()
export class HttpWriteKeepAliveInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (
      CANCELLABLE_METHODS.has(request.method.toUpperCase()) ||
      request.context.get(ALLOW_REQUEST_CANCELLATION)
    ) {
      return next.handle(request);
    }

    return next.handle(request).pipe(keepAlive());
  }
}
